import { palette } from '@/theme/constants';
import { Box, Grid, IconButton, Theme, Typography, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { authSelector, memorySelector, storySelector } from '@/store/selectors';
import Image from 'next/image';
import { FC, useEffect, useRef, useState } from 'react';
import { MuiIconButton } from '@/components';
import { FormikConfig } from './formik';
import {
  setShowMediaButton,
  sendMemory,
  setMediaType,
  updateMemory,
  openModal,
  openSubscriptionPopup,
  updateSubscriptionStatus,
  refreshUserData,
  closeModal,
  setMediaScreenType,
  selectEditMemory,
  updateMemoryViewG,
} from '@/store/actions';
import {
  FetchFileService,
  cdn_url,
  checkRoleAndPermission,
  complementaryDownload,
  complementaryUpload,
  fileConverter,
} from '@/utils';

import MediaUploadStep from '../../../MediaUploadStep';
import { useRouter } from 'next/router';
import { RefreshUserData } from '@/utils/fetchUserData';
import { UseFirstRender } from '@/hooks';
import { SaveMemoryPopup } from '@/components/Popups/SaveMemoryPopup';
import { ErrorPopup } from '@/components/ErrorPopup';
import { LoadingModal } from '../loadingModal';
import { syncAudios, syncImages, syncVideos } from '@/utils/SyncMedia';
import {
  classifyKind,
  removeCrossCategoryDuplicates,

} from '@/utils/complementaryStructure';
import { deriveMemoryType, sanitizeMemoryPayload } from '@/utils/prepareMemoryPayload';

export const Form: FC<any> = ({ submitForm, formRef, setHasChanges, isLoading, pendingMemory, setIsLoading }) => {
  const { memoriesLoaded, memoryData } = useSelector(memorySelector);

  const { story } = useSelector(storySelector);
  const [defaultItem, setDefaultItem] = useState<any>(null);
  const { user } = useSelector(authSelector);
  const dispatch = useDispatch();
  const [showPopup, setShowPopup] = useState(false);
  const [isAllowed, setisAllowed] = useState(true);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [overallPct, setOverallPct] = useState(0);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const router = useRouter();

  const handleClosePopup = () => {
    setShowPopup(false);
    localStorage.removeItem('pendingMemory');
    setIsLoading(false);
  };

  const handleCreateAccount = () => {
    localStorage.setItem('pendingMemory', JSON.stringify(values));
    router.push('/app/register');
  };

  const saveComplementarys = async (data: any) => {
    const { complementaryMedia, complementaryText, mediaFiles, richText } = data;

    const newData: any = { memory_details: {} };
    let totalSize = 0;

    const allMedia = [...(complementaryMedia || []), ...(mediaFiles || [])];

    const mappedMedia = allMedia.map((item: any) => {
      const fileObj = item?.url ? item : item.file || item;
      return {
        file: fileObj,
        isFeatured: item?.isFeatured || false,
        kind: classifyKind(fileObj),
      };
    });

    const imgs = mappedMedia.filter((m) => m.kind === 'image');
    const vids = mappedMedia.filter((m) => m.kind === 'video');
    const auds = mappedMedia.filter((m) => m.kind === 'audio');

    try {
      if (imgs.length > 0) {
        imgs.forEach((it: any) => {
          if (it?.file?.size) totalSize += it.file.size;
        });
        const uploaded = await complementaryUpload(dispatch, imgs, story, false, true);
        const existing = Array.isArray(defaultItem?.memory_details?.complementaryImage)
          ? defaultItem.memory_details.complementaryImage
          : [];
        newData.memory_details.complementaryImage = syncImages(existing, uploaded, mediaFiles);
      } else {
        newData.memory_details.complementaryImage = [];
      }
    } catch {
      dispatch(openModal({ content: 'Image upload failed. Please try again.' }));
      throw new Error('Image upload failed');
    }

    try {
      if (vids.length > 0) {
        vids.forEach((it: any) => {
          if (it?.file?.size) totalSize += it.file.size;
        });
        const uploaded = await complementaryUpload(dispatch, vids, story, true, false, ({ overallPercent }: any) =>
          setOverallPct(overallPercent),
        );
        const existing = Array.isArray(defaultItem?.memory_details?.complementaryVideo)
          ? defaultItem.memory_details.complementaryVideo
          : [];
        newData.memory_details.complementaryVideo = syncVideos(existing, uploaded, mediaFiles);
      } else {
        newData.memory_details.complementaryVideo = [];
      }
    } catch (err) {
      console.error('Video upload error', err);
      dispatch(openModal({ content: 'Video upload failed. Please try again.' }));
      setUploadingVideo(false);
      throw new Error('Video upload failed');
    }

    try {
      if (auds.length > 0) {
        auds.forEach((it: any) => {
          if (it?.file?.size) totalSize += it.file.size;
        });
        const uploaded = await complementaryUpload(dispatch, auds, story, false, false);
        const existing = Array.isArray(defaultItem?.memory_details?.complementaryAudio)
          ? defaultItem.memory_details.complementaryAudio
          : [];
        newData.memory_details.complementaryAudio = syncAudios(existing, uploaded, mediaFiles);
      } else {
        newData.memory_details.complementaryAudio = [];
      }
    } catch (err) {
      console.error('Audio upload error', err);
      dispatch(openModal({ content: 'Audio upload failed. Please try again.' }));
      throw new Error('Audio upload failed');
    }

    removeCrossCategoryDuplicates(newData.memory_details);

    if (Array.isArray(complementaryText) && complementaryText.length > 0) {
      newData.memory_details.complementaryText = complementaryText;
    }
    if (Array.isArray(richText) && richText.length > 0) {
      newData.memory_details.richText = richText;
    }

    let type: 'text' | 'image' | 'video' | 'audio' = 'text';
    if ((newData.memory_details.complementaryImage || []).some((i: any) => i.isFeatured)) type = 'image';
    else if ((newData.memory_details.complementaryVideo || []).some((v: any) => v.isFeatured)) type = 'video';
    else if ((newData.memory_details.complementaryVideo || []).length > 0) type = 'video';
    else if ((newData.memory_details.complementaryAudio || []).length > 0) type = 'audio';

    return { ...newData, totalSize, type };
  };

  const saveMemoryData = async (data: any) => {
    let id: any;
    const newData = { ...data };

    const payload = await sanitizeMemoryPayload({ ...newData, storyTitle: story?.title });
    if (user?.token) {
      try {
        if (defaultItem?.id) {
          newData.id = defaultItem?.id;
          dispatch(
            updateMemory({
              id: defaultItem?.id,
              ...payload,
              router,
              type: deriveMemoryType(payload.memory_details),
              storyId: story?.url,
            }),
          );
          await saveComplementarys(data);
        } else {
          id = await new Promise<string>((resolve, reject) => {
            dispatch(
              sendMemory({
                ...payload,
                isPending: true,
                type: deriveMemoryType(payload.memory_details),
                router,
                storyId: story?.url,
                onCreated: (res: any) => resolve(res?.id),
                onError: (err: any) => reject(err),
              }),
            );
          });
          newData.id = id;
          dispatch(
            updateMemory({
              id: id,
              ...payload,
              type: deriveMemoryType(payload.memory_details),
              router,
              storyId: story?.url,
            }),
          );
          await saveComplementarys(data);
        }
      } catch (error) {
        return;
      }
    } else {
      setIsLoading(false);
      setShowPopup(true);
      setIsLoading(false);
      return;
    }
  };

  const handleSubmit = async (data: any) => {
    if (!user?.token) {
      return saveMemoryData(data);
    }

    try {
      setIsLoading(true);

      dispatch(refreshUserData());
      dispatch(updateSubscriptionStatus({ userId: story?.user?.id }));
      await RefreshUserData(user.token, story?.user?.id);

      data.story_id = story?.id;
      const isOwner = story?.user_id === user?.id;

      const hasPermission = checkRoleAndPermission(
        story?.user?.roles,
        'Subscriber_Individual',
        'CLIENT_MEMORY_CREATE',
        story?.user_id,
      );

      if (!hasPermission) {
        const message = isOwner
          ? 'Your subscription has expired. Please subscribe again to continue.'
          : 'The creator of the story is not a subscriber.';

        dispatch(openModal({ content: message }));

        if (isOwner) {
          setTimeout(() => dispatch(openSubscriptionPopup()), 1000);
        }

        return;
      }

      await saveMemoryData(data);
    } catch (error) {
      dispatch(openModal({ content: 'An unexpected error occurred. Please try again.' }));

      setIsLoading(false);
    }
  };

  const getFileType = (type: string, mediaType: string) => (type === 'image' ? 'image/webp' : mediaType);

  const handleOnTouched = (key: string) => setTouched({ ...touched, [key]: true });

  const {
    values,
    handleSubmit: formikSubmit,
    handleChange: formikHandleChange,
    errors,
    touched,
    setTouched,
    setFieldValue,
    resetForm,
  } = FormikConfig(handleSubmit);

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isAllowed) {
      setisAllowed(true);
      setIsLoading(false);

      setShowPopup(true);
      setIsLoading(false);
    }
  }, [isAllowed]);

  const setDefaults = async () => {
    try {
      if (!defaultItem) {
        setIsLoading(false);
        return;
      }

      const valuesKeys = Object.keys(values || {});
      // set all primitive/top-level fields that exist in form
      Object.keys(defaultItem).forEach((k) => {
        if (valuesKeys.includes(k)) setFieldValue(k, defaultItem[k]);
      });

      // --- read DB memory_details verbatim ---
      const dbDetails = defaultItem?.memory_details || {};
      const dbImgs = Array.isArray(dbDetails?.complementaryImage) ? dbDetails.complementaryImage : [];
      const dbVids = Array.isArray(dbDetails?.complementaryVideo) ? dbDetails.complementaryVideo : [];
      const dbAuds = Array.isArray(dbDetails?.complementaryAudio) ? dbDetails.complementaryAudio : [];

      // keep text fields as-is
      if (Array.isArray(dbDetails?.complementaryText)) {
        setFieldValue('richText', dbDetails.complementaryText);
      }
      if (Array.isArray(dbDetails?.richText)) {
        setFieldValue('richText', dbDetails.richText);
      }

      // make a deep copy so we don’t mutate redux/original refs
      const exactMediaFromDB = [
        ...JSON.parse(JSON.stringify(dbImgs)),
        ...JSON.parse(JSON.stringify(dbVids)),
        ...JSON.parse(JSON.stringify(dbAuds)),
      ];

      // --- OPTIONAL: handle main asset (non-text) for preview only ---
      // we keep it separate & only add if it’s not already present and not featured
      let mainAssetItems: any[] = [];
      let mainAssetUrl: string | null = null;

      if (defaultItem?.asset && memoryData?.mediaType !== 'text') {
        mainAssetUrl = `${cdn_url}${defaultItem.asset}`;
        try {
          const fetched: any = await complementaryDownload(dispatch, defaultItem.asset);
          // ensure array
          const fetchedArr = Array.isArray(fetched) ? fetched : fetched ? [fetched] : [];
          // check if the main asset is already represented & featured in DB media
          const alreadyFeatured = dbVids
            .concat(dbImgs)
            .some((m: any) => m?.isFeatured && cdn_url + (m?.fileName || m?.thumb || '') === mainAssetUrl);
          if (!alreadyFeatured) {
            mainAssetItems = fetchedArr;
          }
          dispatch(setMediaType('media'));
        } catch (e) {
          console.error('Error fetching asset:', e);
        }
      } else if (defaultItem?.type === 'text' && memoryData?.mediaType === 'text') {
        dispatch(setMediaType('text'));
        if (defaultItem?.asset) {
          try {
            setFieldValue('richText', JSON.parse(defaultItem.asset));
          } catch {
            setFieldValue('richText', defaultItem.asset);
          }
        }
      }

      // final mediaFiles = EXACT DB complementary media (+ optional main asset preview if not dup/featured)
      const finalMediaFiles = [...exactMediaFromDB, ...mainAssetItems];

      setFieldValue('mediaFiles', finalMediaFiles);

      // also mirror the DB complementary fields into the form if your form tracks them
      setFieldValue('complementaryImage', dbImgs);
      setFieldValue('complementaryVideo', dbVids);
      setFieldValue('complementaryAudio', dbAuds);

      dispatch(setShowMediaButton());
      setIsLoading(false);
    } catch (error) {
      console.error('Error in setDefaults:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);

    setDefaults();
  }, [defaultItem, memoryData?.mediaType]);

  const handleChange = (e: any) => {
    if (e?.target) {
      if (values[e.target.name] !== e.target.value) {
        dispatch(setHasChanges(true));
      }
      formikHandleChange(e);
    } else {
      const { name, value } = e;
      if (values[name] !== value) {
        dispatch(setHasChanges(true));
      }
      setFieldValue(name, value);
    }
  };

  const selectTypeOfScreen = () => {
    const memoryEdit = memoriesLoaded?.find((item: any) => item.id == router?.query?.memoryId) || pendingMemory;

    if (memoryEdit) {
      setDefaultItem(memoryEdit);
      dispatch(setMediaScreenType('form'));
      dispatch(setMediaType(memoryEdit?.type));

      dispatch(selectEditMemory(null));
      dispatch(updateMemoryViewG(memoryEdit?.user_id));
      return;
    }
  };

  UseFirstRender(() => {
    selectTypeOfScreen();
  }, [router?.query?.memoryId, memoriesLoaded?.length, story?.story_details?.prompts?.length]);

  return (
    <>
      {/* {isLoading && isEditing ? (
        <Box width={'100%'} height={'100vh'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
          <Image src='/images/Loading.svg' quality={'100'} alt='loading' fill style={{ objectFit: 'cover' }} />
        </Box>
      ) : ( */}
      <>
        <Box
          px={'1rem'}
          sx={{
            width: '100%',
            height: isMobile ? 80 : 160,
            position: isMobile ? 'relative' : 'relative',
            top: 0,
            zIndex: 10,
            overflow: 'hidden',

            backdropFilter: 'blur(20px)',
          }}>
          <Box
            sx={{
              width: '100%',

              maxWidth: '1200px',
              margin: '0 auto',
              position: 'relative',
              top: isMobile ? 30 : 114,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <IconButton
              onClick={() => {
                router.push(`/app/story/${story?.url}`);
              }}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                color: 'white',
              }}>
              <Image src='/icons/left-arrow.svg' width={20} height={20} alt='close' />
            </IconButton>

            {defaultItem ? (
              <Typography color={palette?.white} align='center' fontSize='26px'>
                Create Memory
              </Typography>
            ) : (
              <Typography color={palette?.white} align='center' mt={1} fontSize='1rem'>
                {story?.title || values?.title}
              </Typography>
            )}
          </Box>

          <>
            {/* { coverLoading &&  ( */}
            <Box
              sx={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                backgroundSize: '200% 100%',
                animation: 'loadingShimmer 3s infinite',
              }}
            />
            {!isMobile && story?.cover_image && (
              <Image
                priority
                src={`${cdn_url}${story.cover_image}`}
                fill={true}
                alt='hello'
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  position: 'absolute',
                  top: 0,
                  left: 0,

                  transition: 'opacity 0.2s ease-in-out',
                }}
              />
            )}
            <>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '1200px',
                  margin: '0 auto',
                  position: 'relative',
                  top: 150,
                  zIndex: 10,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}></Box>
            </>

            {/* Blur Overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backdropFilter: 'blur(60px)',
                zIndex: 3,
              }}
            />
          </>
        </Box>

        <form
          ref={formRef}
          onSubmit={formikSubmit}
          style={{ width: '100%', maxWidth: '600px', margin: '0 auto', paddingTop: isMobile ? '16px' : '5rem' }}>
          <Grid container spacing={2}>
            <MediaUploadStep
              setisAllowed={setisAllowed}
              errors={errors}
              touched={touched}
              handleOnTouched={handleOnTouched}
              handleChange={handleChange}
              values={values}
              setFieldValue={setFieldValue}
            />
          </Grid>
        </form>
        <Box display='flex' justifyContent='center' my={2}>
          <MuiIconButton
            icon='/icons/tick'
            label={defaultItem?.id ? 'Update Memory' : ' Save Memory'}
            loading={isLoading}
            altIcon='Memvy'
            iconPosition='left'
            direction='row'
            background='rgba(33, 45, 109, 1)'
            width={!isMobile ? '353px' : '310px'}
            isRounded={false}
            height={!isMobile ? '58px' : '58px'}
            iconHeight={12.8}
            iconWidth={8.8}
            sx={{
              marginTop: '10px',
              borderRadius: '10px',
              gap: '1rem',
            }}
            method={submitForm}
          />
        </Box>
      </>
      <ErrorPopup />
      <LoadingModal open={overallPct > 0} percent={overallPct} />
      <SaveMemoryPopup
        data={values}
        open={showPopup}
        onClose={() => handleClosePopup()}
        onCreateAccount={handleCreateAccount}
      />
    </>
  );
};
